import { useCallback, useRef, useState } from "react";
import { ASR_AUDIO_CONSTRAINTS } from "./mic";

/**
 * 自由モード用の録音フック (PRODUCT_DEV-574 項目16)。
 * MediaRecorder で音声を収録し、停止時に base64 + mimeType を返す。
 * 文字起こしは停止後に Whisper (survey /transcribe) へ送る想定 (ライブ字幕なし)。
 */
export type RecorderStatus = "idle" | "recording" | "error";

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("音声の読み込みに失敗しました"));
    r.onloadend = () => {
      const s = String(r.result);
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.readAsDataURL(blob);
  });
}

export function useMediaRecorder() {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mrRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef("audio/webm");

  const cleanup = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mrRef.current = null;
    setStatus("idle");
    setPaused(false);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setPaused(false);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: ASR_AUDIO_CONSTRAINTS,
      });
      streamRef.current = stream;
      const preferred = ["audio/webm", "audio/mp4", "audio/ogg"];
      const supported = preferred.find(
        (m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m),
      );
      const mr = supported ? new MediaRecorder(stream, { mimeType: supported }) : new MediaRecorder(stream);
      mimeRef.current = mr.mimeType || supported || "audio/webm";
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mrRef.current = mr;
      mr.start();
      setStatus("recording");
    } catch (e) {
      setError(String(e));
      setStatus("error");
    }
  }, []);

  const pause = useCallback(() => {
    try { mrRef.current?.pause(); } catch { /* ignore */ }
    setPaused(true);
  }, []);
  const resume = useCallback(() => {
    try { mrRef.current?.resume(); } catch { /* ignore */ }
    setPaused(false);
  }, []);

  /** 録音停止。収録音声を base64 で返す (無音/未収録は null)。 */
  const stop = useCallback((): Promise<{ base64: string; mimeType: string } | null> => {
    const mr = mrRef.current;
    if (!mr || mr.state === "inactive") {
      cleanup();
      return Promise.resolve(null);
    }
    return new Promise((resolve) => {
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        const mimeType = mimeRef.current;
        cleanup();
        if (blob.size === 0) { resolve(null); return; }
        try {
          const base64 = await blobToBase64(blob);
          resolve({ base64, mimeType });
        } catch {
          resolve(null);
        }
      };
      try { mr.stop(); } catch { cleanup(); resolve(null); }
    });
  }, [cleanup]);

  return { status, paused, error, start, pause, resume, stop };
}
